import styled, { css, keyframes } from 'styled-components';

import { Filters } from '../newDashboardStyles';
import { statusTextColor } from '../../../utils/constants/statusMonitoring';

const fontSizes = {
  extra_small: '11px',
  small: '13px',
  medium: '15px',
  large: '18px',
  extra_large: '23px',
  responsive: {
    small: '11px',
    medium: '12px',
    extra_large: '21px',
  }
};

const colors = {
  ivory_pale: '#fcf7f6',
  snowy_white: '#f6f6f6',
  white: '#fff',
  purple: '#9c179e',
  charcoal_gray: '#484848',
  sky_blue: '#41b8d5',
  sunflower_yellow: '#fce83a',
  olive_green: '#c8e066',
  black: '#000',
  light_gray: '#c9c7d0',
};

const fontFamilies = {
  oswald: 'Oswald',
  lato: 'Lato',
};

const applyEllipsis = (lines) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  word-break: break-word;
  overflow: hidden;
`;

export const Container = styled.div`
  height: 100vh;
  width: 95%;
  margin-left: 5%;
  padding: 2% 2% 2% 2%;
  background: ${colors.ivory_pale};
  display: flex;
  flex-direction: column;
  gap: 2%;
  scrollbar-width: none;
  @media (max-width: 425px) {
    margin-left: 0;
    width: 100%;
    margin-top: 85px;
    height: calc(100vh - 85px);
  }
  @media (max-width: 980px) {
    overflow-y: auto;
  }
`;

export const Header = styled.div`
  padding: 2%;
  border-radius: 24px;
  background: ${colors.white};
  display: flex;
  flex-direction: column;
  gap: 24px;
  h1 {
    font-size: ${fontSizes.extra_large};
    font-family: ${fontFamilies.oswald}, sans-serif;
    text-transform: uppercase;
  }
  @media (max-width: 980px) {
    h1 {
      font-size: ${fontSizes.responsive.extra_large};
    }
  }
  .ant-select-selection--multiple .ant-select-selection__rendered > ul > li {
    max-width: 180px;
  }
  .ant-select-selection--multiple .ant-select-selection__placeholder {
    margin-top: -10px;
  }
  .ant-select-allow-clear .ant-select-selection--multiple .ant-select-selection__rendered, .ant-select-show-arrow .ant-select-selection--multiple .ant-select-selection__rendered {
    height: 30px;
    overflow: auto;
    scrollbar-width: none;
  }
`;

export const Content = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  gap: 2%;
  overflow: hidden;
  @media (max-width: 980px) {
    flex-direction: column;
    overflow: visible;
  }
`;

export const Sidebar = styled.div`
  height: 100%;
  width: 30%;
  padding: 12px;
  border-radius: 24px;
  background: ${colors.white};
  color: ${colors.black};
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  .pagination-wrapper {
    margin-top: auto;
    height: ${props => props.pageNumberHeight}px;
    display: grid;
    place-items: center;
    .ant-pagination-item {
      font-family: ${fontFamilies.lato}, sans-serif;
      a {
        text-decoration: none;
      }
    }
    .ant-pagination-item-active {
      border-color: ${colors.sky_blue};
      a {
        color: ${colors.sky_blue};
      }
    }
  }
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 980px) {
    height: auto;
    width: 100%;
  }
`;

const batchItemLeftAnimation = keyframes`
  0% {
    opacity: 0;
    left: -100px;
  }
  100% {
    opacity: 1;
    left: 0px;
  }
`;

const batchItemAnimation = keyframes`
  0% {
    opacity: 0;
    top: 100px;
    // transform: scale(0);
  }
  100% {
    opacity: 1;
    top: 0px;
    // transform: scale(1);
  }
`;

export const BatchItem = styled.div`
  min-height: 98px;
  padding: 12px;
  border-radius: 12px;
  font-family: ${fontFamilies.lato}, sans-serif;
  background: ${props => (props.active ? colors.purple : colors.snowy_white)};
  color: ${props => props.active && colors.white};
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 6px;
  /*transform: scale(1);
  transition: transform 0.1s linear;*/
  transition: background 0.5s ease-in-out;
  :hover {
    cursor: pointer;
  }
  &.animate {
    position: relative;
    opacity: 0;
    // transform-origin: 0 0;
    animation: ${batchItemAnimation} 0.5s ease-in-out;
    animation-delay: ${props => `${props.index === 0 ? 0 : props.index * 0.3}s`};
    animation-fill-mode: forwards;
  }
  &.clicking {
    transform: scale(0.98);
  }
  .batch-title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 6px;
    h6 {
      font-size: ${fontSizes.medium};
      text-transform: uppercase;
      color: ${props => props.active && colors.white};
      ${applyEllipsis(2)}
    }
    p {
      min-width: 85px;
      font-size: ${fontSizes.small};
      text-align: right;
    }
    strong {
      color: ${colors.black};
    }
  }
  .progress-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: ${fontSizes.small};
  }
  .batch-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }
  @media (max-width: 980px) {
    .batch-title {
      p {
        font-size: ${fontSizes.responsive.small};
      }
  }
`;

export const ProgressBar = styled.div`
  height: 15px;
  width: 150px;
  background: ${colors.charcoal_gray};
  border-radius: 12px;
  overflow: hidden;
  div {
    height: 15px;
    width: ${props => (props.value ? props.value : 0)}%;
    background: ${colors.sky_blue};
    border-radius: 12px;
    transition: all 1s ease-in;
  }
`;

const arrowAnimation = keyframes`
  0% {
    opacity: 0;
    marker-end: none;
    stroke-dashoffset: 250;
  }
  100% {
    opacity: 1;
    marker-end: '';
    stroke-dashoffset: 0;
  }
`;

const popInAnim = keyframes`
  100% {
    transform: scale(1);
  }
`;
const fadeInAnim = keyframes`
  100% {
    opacity: 1;
    transform: translateY(0%);
  }
`;

export const Info = styled.div`
  height: 100%;
  width: 70%;
  font-family: ${fontFamilies.lato}, sans-serif;
  // overflow-y: auto;
  .title-container {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: ${fontSizes.small};
    p {
      font-size: ${fontSizes.small};
      color: ${colors.black};
      text-transform: uppercase;
      white-space: nowrap;
    }
  }
  .arrows {
    opacity: 1;
    > path {
      opacity: 0;
      // stroke-dasharray: 300;
      stroke-dashoffset: 300;
      animation: ${arrowAnimation} 1s linear forwards;
    }
  }
  .arrow-1 {
    > path {
      animation-delay: 0s;
    }
  }
  .arrow-2 {
    > path {
      animation-delay: 0.5s;
    }
  }
  .arrow-3, .arrow-4, .arrow-5, .arrow-6 {
    > path {
      animation: ${arrowAnimation} 0.5s linear forwards;
      animation-delay: 1s;
    }
  }
  /*.arrow-4 {
    > path {
      animation-delay: 1.5s;
    }
  }
  .arrow-5 {
    > path {
      animation-delay: 2s;
    }
  }
  .arrow-6 {
    > path {
      animation-delay: 2.5s;
    }
  }*/
  .approval-status {
    font-size: ${fontSizes.small};
    padding: 8px 0;
  }
  .chart-wrapper {
    width: fit-content;
    margin-block: 24px;
    padding: 24px;
    background: ${colors.snowy_white};
    border-radius: 24px;
  }
  h4 {
    font-size: ${fontSizes.large};
    font-weight: 600;
    text-transform: uppercase;
    ${applyEllipsis(2)}
  }
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 980px) {
    width: 100%;
    .title-container {
      p {
        font-size: ${fontSizes.responsive.small};
      }
    }
  }
`;

export const IconWrapper = styled.div`
  height: ${props => (props.size ? props.size + 'px' : '30px')};
  width: ${props => (props.size ? props.size + 'px' : '30px')};
  background: ${props => (props.background ? props.background : colors.sunflower_yellow)};
  border-radius: 17.5px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  ${props =>
    props.absolute &&
    `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    `};
  ${props => props.bottom && `
    top: auto;
    bottom: -10%;
  `}
`;

export const JobCardsLayout = styled.div`
  padding: 24px;
  display: grid;
  align-items: start;
  justify-content: start;
  gap: 60px 100px;
  overflow: auto;
  height: 100%;
  scrollbar-width: none;
`;

export const ScrollableContainer = styled.div`
  height: calc(100% - 40px);
  overflow: auto;
  padding-bottom: 20px;

  > div {
    height: 100%;
  }
`;

const cardAnimation = keyframes`
  0% {
    opacity: 0;
    // transform: scale(0.7);
  }
/*  75% {
    transform: scale(1.2);
  }*/
  100% {
    opacity: 1;
    // transform: scale(1);
  }
`;

export const JobCard = styled.div`
  position: ${props => props.relative && 'relative'};
  width: ${props => (props.width ? props.width : 180)}px;
  background: ${colors.white};
  padding: 20px;
  border-radius: 18px;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
  grid-column: ${props => props.position.column};
  grid-row: ${props => props.position.row};
  // transform: scale(0);
  opacity: 0;
  // transform-origin: 0 50%;
  animation: ${cardAnimation} 0.8s forwards;
  animation-delay: ${props => `${props.index < 2 ? props.index * 0.5 : ((props.index - 1) * 0.1) + 0.5}s`};
  :hover {
    cursor: pointer;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    -webkit-transition: box-shadow 0.6s ease-in;
  }  
    transition: box-shadow 0.6s ease-in-out;
    -webkit-transition: box-shadow 0.6s ease-in-out;
`;

export const JobTitle = styled.div`
  background: ${colors.black};
  color: ${colors.white};
  text-align: center;
  text-transform: uppercase;
  padding: 8px 4px;
  font-size: ${fontSizes.small};
  @media (max-width: 980px) {
    font-size: ${fontSizes.responsive.medium};
  }
`;

export const JobCardContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 18px;
  padding: 18px 4px 0 4px;
`;

export const JobCount = styled.h1`
  font-size: ${fontSizes.extra_large};
  font-weight: 500;
  text-align: center;
  font-family: ${fontFamilies.oswald}, sans-serif;
  @media (max-width: 980px) {
    font-size: ${fontSizes.responsive.extra_large};
  }
`;

export const PrinterAndTickIcon = styled.div`
  position: relative;
  div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export const JobCardContentText = styled.p`
  color: ${colors.black};
  text-align: center;
  @media (max-width: 980px) {
    font-size: ${fontSizes.small};
  }
`;

const stepsAnimation = keyframes`
  0% {
    stroke-dashoffset: 8.5;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const levelTextAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

export const StepContainer = styled.div`
  .steps {
    line {
      stroke: #d8d6d8;
      // stroke: black;
      &.animate {
        stroke: #9c179e;
        stroke-dasharray: 8.5;
        stroke-dashoffset: 8.5;
        animation: ${stepsAnimation} 1s forwards;
      }
    }
    line.animate.delay {
       animation-delay: 1s;
    }
    text {
      font-size: 3px;
      opacity: 0;
      animation: ${levelTextAnimation} 1s forwards;
    }
    image {
      opacity: 0;
      animation: ${levelTextAnimation} 1s forwards;
    }
  }
`;

export const Step = styled.div`
  position: relative;
  height: ${props => !props.isLastStep && props.size}px;
  width: ${props => props.size}px;
  border-right: ${props => !props.isLastStep && props.strokeWidth}px solid;
  border-bottom: ${props => props.strokeWidth}px solid;
  border-color: ${props => props.isCompleted && props.primaryColor};
  margin-left: ${props => (props.stepId - 1) * props.size}px;
  div {
    position: absolute;
    right: ${props => props.strokeWidth}px;
    bottom: ${props => props.strokeWidth}px;
    display: flex;
    gap: 2px;
    align-items: center;
    justify-content: flex-end;
    p {
      color: ${colors.black};
      font-size: ${fontSizes.small};
      font-weight: 700;
      white-space: nowrap;
    }
  }
`;

export const JobInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export const JobInfoTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  .channel-name {
    font-size: ${fontSizes.small};
  }
  h3 {
    font-size: ${fontSizes.large};
    font-weight: 500;
    font-family: ${fontFamilies.oswald}, sans-serif;
  }
  .channel-icon {
    background: ${colors.olive_green};
    border-radius: 8px;
    height: 32px;
    width: 32px;
    display: grid;
    place-content: center;
  }
  .job-status {
    font-size: ${fontSizes.extra_small};
    color: ${colors.black};
    font-weight: 700;
    text-transform: uppercase;
  }
`;

const slideIn = keyframes`
  0% {
    left: -10%;
    opacity: 0;
  }
  75% {
    left: 1%;
    opacity: 0;
  }
  100% {
    left: 0;
    opacity: 1;
  }
`;

export const StatusFilters = styled(Filters)`
  .ant-col {
    animation: ${slideIn} 0.4s ease-in;
  }
`;

export const BatchCurrentStatus = styled.div`
  background: ${props => props.bg ? props.bg : '#fcf7f6'};
  color: ${props => statusTextColor.includes(props.status) ? colors.white : colors.black};
  width: fit-content;
  padding: 4px 12px;
  font-size: ${fontSizes.extra_small};
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 5px;
`;

export const NoRecord = styled.div`
  background: white;
  border-radius: 24px;
  width: 100%;
  height: 100%;
`;

export const NoApproverContent = styled.div`
  text-align: center;
  .ant-alert-warning {
    width: fit-content;
    margin-inline: auto;
    padding: 8px 8px 8px 24px;
    transform: scale(0);
    animation: ${popInAnim} 0.5s ease forwards;
  }
  .ant-alert-icon {
    top: 12.5px;
    left: 6px;
  }
  .ant-alert-message {
    font-size: 13px;
  }
  .description {
    font-size: 13px;
    line-height: 1.3;
    font-family: Lato;
    color: #000;
    text-align: center;
    padding: 12px 8px;
    opacity: 0;
    transform: translateY(100%);
    animation: ${fadeInAnim} 0.5s 0.2s ease forwards;
  }
  .buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .ant-btn, .ant-btn:hover, .ant-btn:focus {
    padding: 2px 8px;
    border: none;
    box-shadow: rgb(38, 57, 77) 0px 4px 8px -5px;
    font-size: 13px;
    text-transform: uppercase;
    font-weight: bold;
    font-family: Lato !important;
    background: darkslategray;
    color: #f1f1f1;
    will-change: transform;
    opacity: 0;
    transform: translateY(100%);
  }
  .ant-btn:hover {
    transform: scale(1.05);
  }
  .primary, .primary:hover, .primary:focus {
    background: yellow;
    color: #333;
  }
  .ant-btn.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 22px;
  }
  .ant-btn:nth-child(1) {
    animation: ${fadeInAnim} 0.5s 0.4s ease forwards;
  }
  .ant-btn:nth-child(2) {
    animation: ${fadeInAnim} 0.5s 0.6s ease forwards;
  }
`;